import { CdkConnectedOverlay, ConnectedPosition, OverlayRef } from '@angular/cdk/overlay';
import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, Renderer2, SimpleChanges, ViewChild, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject, Subscription, fromEvent } from 'rxjs';
import { debounceTime, filter, map, takeUntil } from 'rxjs/operators';
import { SPP_OVERLAY_POSITION_DROPDOWN } from '../../common-ui-common.config';
import { LanguageService } from '../../i18n/language.service';
import { KeyboardListDirective } from '../../keyboard/keyboard-list.directive';
import { KeyboardUtils } from '../../keyboard/keyboard.utils';
import { LoggingService } from '../../utils/logging.service';
import { I18N } from './form-autocomplete.i18n';

/**
 * Array von Objekten, wobei jedes Objekt 1-2 Keys für den
 * Model-Wert und den Display-Wert hat. Muss mit {@link FormAutocompleteComponent#modelValue}
 * und {@link FormAutocompleteComponent#displayValue} übereinstimmen.
 *
 * Beispiele:
 * <pre>
 * <common-form-autocomplete
 *     modelValue="modelValue"
 *     displayValue="displayValue"
 *     [selectableItems]="[{modelValue: one, displayValue: 'One'}, ...]"
 * ></common-form-autocomplete>
 * </pre>
 * oder
 * <pre>
 * <common-form-autocomplete
 *     modelValue="val"
 *     displayValue="val"
 *     [selectableItems]="[{val: 'ONE'}, {val: 'TWO'}]"
 * ></common-form-autocomplete>
 *  </pre>
 */
export type AutocompleteItem = {
    [key: string]: any,
};

/**
 * Diese Komponente ist eine spezielle Formular Input Komponente welche über einen Drop-Down alle möglichen Ergebnisse
 * zu einer bestimmten Suche anzeigt.
 *
 * @author: femu
 */
@Component({
    selector: 'common-form-autocomplete',
    templateUrl: 'form-autocomplete.component.html',
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => FormAutocompleteComponent),
        multi: true
    }]
})
export class FormAutocompleteComponent implements AfterViewInit, OnDestroy, ControlValueAccessor, OnChanges {
    /**
     * Die Items, die ausgewählt werden können.
     */
    @Input() selectableItems: AutocompleteItem[];
    /**
     * Name des Properties in {@link AutocompleteItem} für den Display-Value.
     */
    @Input() displayValue: string;
    /**
     * Name des Properties in {@link AutocompleteItem} für den Model-Value.
     */
    @Input() modelValue: string;
    /**
     * Ob ein exisitierender Wert im Model auch dann angezeigt werden soll, wenn er in den {@link #selectableItems} nicht
     * mehr vorkommt. In den Fällen kann kein "displayValue" ermittelt werden kann, darum wird stattdessen der
     * "modelValue" in Klammern dargestellt. Ist diese Option nicht gesetzt, dann wird gar nichts dargestellt
     * (Eingabefeld bleibt leer trotz gesetztem Wert im Model)
     */
    @Input() showInvalidModelValues: boolean;
    /**
     * Die Minimale Länge der Suche ab der gesucht werden soll. (optional)
     */
    @Input() minChars: number;
    /**
     * Der Platzhalte-Text. (optional)
     */
    @Input() placeholder: string = '';
    /**
     * Text der angezeigt werden soll wenn kein Ergebniss gefunden wurde. (optional)
     */
    @Input() notFoundText: string;
    /**
     * Der Text für das Info-Icon.
     */
    @Input() dataToolTip: string;
    /**
     * Klasse, die an das Input-Element angefügt wird. z.B. "large"
     */
    @Input() class: string;
    /**
     * Ob das Input-Item disabled ist.
     */
    @Input() disabled: boolean;
    /**
     * Gibt ein Event aus, wenn sich der Suchtext geändert hat.
     */
    @Output() searchTextChange: EventEmitter<string> = new EventEmitter();

    @ViewChild('searchInput') searchInput: ElementRef;

    @ViewChild('keyboardList') keyboardList: KeyboardListDirective<AutocompleteItem> | null;

    @ViewChild('originEle') originEle: ElementRef;

    @ViewChild(CdkConnectedOverlay) overlay: CdkConnectedOverlay;

    selectedItem: any;
    showResult: boolean = false;
    i18n: any;

    overlayPosition: ConnectedPosition[] = SPP_OVERLAY_POSITION_DROPDOWN;

    private languageSubscription: Subscription;
    private _search: string;
    private valueForDelayedItems: string;
    // Callbacks für den ControlValueAccessor
    private onChangeCallback: (_: any) => {};
    private onTouchedCallback: () => {};

    private ngUnsubscribe$: Subject<void> = new Subject<void>();

    private clickEventHandler: () => void;

    private offsetStart: number;

    private overlayRef: OverlayRef;

    constructor(private languageService: LanguageService,
                private renderer: Renderer2,
                private elementRef: ElementRef,
                private logger: LoggingService) {
        this.languageSubscription = languageService.registerForLanguageChanged(this, 'i18n', I18N);
    }

    ngAfterViewInit() {
        this.overlay.attach.subscribe(() => this.overlayRef = this.overlay.overlayRef);
        this.overlay.detach.subscribe(() => this.overlayRef = null);

        fromEvent(this.searchInput.nativeElement, 'keyup')
            .pipe(
                debounceTime(250),
                map((e: any) => e.target.value),
                filter(x => this.searchValid(x)),
                takeUntil(this.ngUnsubscribe$)
            )
            .subscribe(value => {
                this.searchTextChange.emit(value);
                this.correctOverlayPosition();
            });

        fromEvent(this.searchInput.nativeElement, 'keydown')
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe((e: Event) => {
                if (this.showResult === false && KeyboardUtils.keyboardEventSearchable(e)) {
                    this.showResult = true;
                }
                if (KeyboardUtils.isKeyboardAction(e, 'down')) {
                    this.showResult = true;
                    e.preventDefault();
                }
            });
        this.addClickOuterEventHandler();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['selectableItems']) {
            this.correctOverlayPosition();
        }
        if (this.selectableItems && this.valueForDelayedItems) {
            this.writeValue(this.valueForDelayedItems);
            this.valueForDelayedItems = null;
        }
    }

    ngOnDestroy() {
        this.ngUnsubscribe$.next();
        this.ngUnsubscribe$.complete();
        this.languageSubscription.unsubscribe();
        if (this.clickEventHandler) {
            this.clickEventHandler();
            this.clickEventHandler = undefined;
        }
    }


    @HostListener('click')
    onClick() {
        if (this.showResult === false) {
            this.showResult = true;
        } else {
            this.correctOverlayPosition();
        }
    }

    onKeyboardBlur() {
        this.searchInput.nativeElement.focus();
    }

    onInputArrowDown(): void {
        this.keyboardList?.moveToFirst();
    }

    onSelect(event: KeyboardEvent | MouseEvent, item: object) {
        event.preventDefault();
        // Schreibe item oder ModelValue ins Model.
        if (this.onChangeCallback) {
            // todo warum kann das undefined sein, seit dem wir CDK verwenden?
            this.modelValue ? this.onChangeCallback(item[this.modelValue]) : this.onChangeCallback(item);
        }
        this.selectedItem = item;
        this.showResult = false;
    }

    onDetach(): void {
        // Focus will be loss while overlay is closing. We need to focus the input element manually.
        // Otherwise keyboard navigation will start from the beginning.
        this.searchInput.nativeElement.focus();
    }

    onInputTab(event: Event): void {
        if (this.showResult) {
            event.preventDefault();
            this.keyboardList?.moveToFirst();
        }
    }

    onResetIconClick(event: Event) {
        event.preventDefault();
        this.search = '';
        this.searchTextChange.next('');
        this.correctOverlayPosition();
        this.searchInput.nativeElement.focus();
    }

    get notFound() {
        if (this.searchValid(this._search) && this.selectableItems && this.selectableItems.length === 0) {
            return this.notFoundText ? this.notFoundText : this.i18n['notFound'];
        } else if (this.minChars && this._search && this._search.length < this.minChars) {
            return this.i18n['notEnoughChars'].replace('{}', this.minChars);
        }
        return null;
    }

    get selectableItemsExist() {
        return this.selectableItems && this.selectableItems.length > 0;
    }

    get search() {
        return this.selectedItem ? this.selectedItem[this.displayValue] : this._search;
    }

    set search(search) {
        this.resetSearch();
        this._search = search;
    }

    // ** Model to View Handler */
    writeValue(value: any) {
        if (this.modelValue) {
            if (!this.selectableItemsExist) {
                this.valueForDelayedItems = value;
                return;
            }
            this.selectedItem = this.selectableItems.find(item => item[this.modelValue] === value)
                // Items, die nicht mehr zur Auswahl stehen, trotzdem anzeigen. Dafür Item konstruieren.
                || this.showInvalidModelValues && { [this.modelValue]: value, [this.displayValue]: '(' + value + ')' }
                || null;
        } else {
            this.selectedItem = value ? value : null;
        }
        this._search = null; // damit bei einem reset des Models auch die letzte Suche nicht mehr angezeigt wird.
    }

    /** View to Model Handler */
    registerOnChange(fn: any) {
        this.onChangeCallback = fn;
    }

    registerOnTouched(fn: any) {
        this.onTouchedCallback = fn;
    }

    setDisabledState(isDisabled: boolean) {
        this.disabled = isDisabled;
    }

    private correctOverlayPosition() {
        if (this.showResult && this.selectableItems && this.selectableItems.length) {
            const offsetNext = this.originEle ? this.originEle.nativeElement.offsetTop : this.offsetStart;
            if (this.offsetStart && this.offsetStart !== offsetNext && this.overlayRef) {
                this.logger.debug('Overlay-Position wird neu berechnet');
                this.overlayRef.updatePosition();
            }
            this.offsetStart = offsetNext;
        }
    }

    private searchValid(value): boolean {
        return this.minChars ? value && value.length >= this.minChars : !(value === undefined || value === null);
    }

    private resetSearch() {
        if (this.selectedItem) {
            this.selectedItem = null;
            // Wird gemacht damit die Required-Validierung funktioniert.
            if (this.onChangeCallback) {
                // todo warum kann das undefined sein, seit dem wir CDK verwenden?
                this.onChangeCallback('');
                this.correctOverlayPosition();
            }
        }
    }

    private addClickOuterEventHandler() {
        setTimeout(() => {
            this.clickEventHandler = this.renderer.listen('document', 'click', e => {
                if (!this.elementRef.nativeElement.contains(e.target)) {
                    this.showResult = false;
                }
            });
        });
    }
}

